import {exhaustive} from '@freckle/exhaustive'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import type {SummaryT} from '@freckle/school/ts/common/models/summary'
import {
  Routes,
  TEACHER_FILTER_QUERY_PARAM_TAG,
  STUDENT_FILTER_QUERY_PARAM_TAG,
  COURSE_FILTER_QUERY_PARAM_TAG,
} from '@freckle/school/ts/common/routers/routes'
import type {AdditionalFiltersQueryParamsT} from '@freckle/school/ts/common/routers/routes'
import type {TabKeyT} from '@freckle/school/ts/manage-teachers/components/manage-teachers/utils'
import {IDPSyncPopover} from '@freckle/school/ts/manage-teachers/components/summary/idp-sync-popover'
import type {CountTypeT} from '@freckle/school/ts/manage-teachers/components/summary/summary-panel'
import {SummaryPanel} from '@freckle/school/ts/manage-teachers/components/summary/summary-panel'
import {TeacherSignupPopover} from './teacher-signup-popover'
import {panels} from './summary.module.scss'

type Props = {
  currentSchoolIds: Array<number> | undefined | null
  summary: SummaryT
  scrollToTabs: () => void
}

export function Summary(props: Props) {
  const {currentSchoolIds, summary, scrollToTabs} = props
  const {teacherCounts, studentCounts, courseCounts} = summary
  const {t} = useSafeTranslation()

  const sortFirstType = getSortFirstType(summary)

  return (
    <div className={panels}>
      <SummaryPanel
        title={t('MANAGE_TEACHERS_TEACHER_SUMMARY_PANEL_TITLE')}
        linkUrl={dashboardWithTab(currentSchoolIds, 'teacher-tab')}
        counts={[
          {
            type: 'admin',
            count: teacherCounts.upload,
            linkUrl: dashboardWithTab(currentSchoolIds, 'teacher-tab', {
              tag: TEACHER_FILTER_QUERY_PARAM_TAG,
              identityProviders: ['csv'],
            }),
          },
          {
            type: 'teacher',
            count: teacherCounts.manual + teacherCounts.unknown + teacherCounts.idpSync,
            linkUrl: dashboardWithTab(currentSchoolIds, 'teacher-tab', {
              tag: TEACHER_FILTER_QUERY_PARAM_TAG,
              identityProviders: ['none'],
            }),
            popOver: <TeacherSignupPopover id="teacher-summary-teacher-signup" />,
          },
          {
            type: 'classlink',
            count: teacherCounts.classlink,
            linkUrl: dashboardWithTab(currentSchoolIds, 'teacher-tab', {
              tag: TEACHER_FILTER_QUERY_PARAM_TAG,
              identityProviders: ['class-link'],
            }),
          },
          {
            type: 'clever',
            count: teacherCounts.clever,
            linkUrl: dashboardWithTab(currentSchoolIds, 'teacher-tab', {
              tag: TEACHER_FILTER_QUERY_PARAM_TAG,
              identityProviders: ['clever'],
            }),
          },
          {
            type: 'renaissance',
            count: teacherCounts.renaissance,
            linkUrl: dashboardWithTab(currentSchoolIds, 'teacher-tab', {
              tag: TEACHER_FILTER_QUERY_PARAM_TAG,
              identityProviders: ['renaissance'],
            }),
          },
        ]}
        scrollToTabs={scrollToTabs}
        sortFirstType={sortFirstType}
      />
      <SummaryPanel
        title={t('MANAGE_TEACHERS_STUDENT_SUMMARY_PANEL_TITLE')}
        linkUrl={dashboardWithTab(currentSchoolIds, 'student-tab')}
        counts={[
          {
            type: 'admin',
            count: studentCounts.upload,
            linkUrl: dashboardWithTab(currentSchoolIds, 'student-tab', {
              tag: STUDENT_FILTER_QUERY_PARAM_TAG,
              identityProviders: ['csv'],
            }),
          },
          {
            type: 'teacher',
            count: studentCounts.manual + studentCounts.unknown,
            linkUrl: dashboardWithTab(currentSchoolIds, 'student-tab', {
              tag: STUDENT_FILTER_QUERY_PARAM_TAG,
              identityProviders: ['none'],
            }),
            popOver: <TeacherSignupPopover id="student-summary-teacher-sign-up" />,
          },
          {
            type: 'classlink',
            count: studentCounts.classlink,
            linkUrl: dashboardWithTab(currentSchoolIds, 'student-tab', {
              tag: STUDENT_FILTER_QUERY_PARAM_TAG,
              identityProviders: ['class-link'],
            }),
          },
          {
            type: 'clever',
            count: studentCounts.clever,
            linkUrl: dashboardWithTab(currentSchoolIds, 'student-tab', {
              tag: STUDENT_FILTER_QUERY_PARAM_TAG,
              identityProviders: ['clever'],
            }),
          },
          {
            type: 'renaissance',
            count: studentCounts.renaissance,
            linkUrl: dashboardWithTab(currentSchoolIds, 'student-tab', {
              tag: STUDENT_FILTER_QUERY_PARAM_TAG,
              identityProviders: ['renaissance'],
            }),
          },
          {
            type: 'idpsync',
            count: studentCounts.idpSync,
            linkUrl: dashboardWithTab(currentSchoolIds, 'student-tab', {
              tag: STUDENT_FILTER_QUERY_PARAM_TAG,
              identityProviders: ['none'],
            }),
            popOver: <IDPSyncPopover id="student-summary-idp-sync" />,
          },
        ]}
        scrollToTabs={scrollToTabs}
        sortFirstType={sortFirstType}
      />
      <SummaryPanel
        title={t('MANAGE_TEACHERS_COURSE_SUMMARY_PANEL_TITLE')}
        linkUrl={dashboardWithTab(currentSchoolIds, 'course-tab')}
        counts={[
          {
            type: 'admin',
            count: courseCounts.upload,
            linkUrl: dashboardWithTab(currentSchoolIds, 'course-tab', {
              tag: COURSE_FILTER_QUERY_PARAM_TAG,
              identityProviders: ['csv'],
            }),
          },
          {
            type: 'teacher',
            count: courseCounts.teacher,
            linkUrl: dashboardWithTab(currentSchoolIds, 'course-tab', {
              tag: COURSE_FILTER_QUERY_PARAM_TAG,
              identityProviders: ['none'],
            }),
            popOver: <TeacherSignupPopover id="course-summary-teacher-signup" />,
          },
          {
            type: 'classlink',
            count: courseCounts.classlink,
            linkUrl: dashboardWithTab(currentSchoolIds, 'course-tab', {
              tag: COURSE_FILTER_QUERY_PARAM_TAG,
              identityProviders: ['class-link'],
            }),
          },
          {
            type: 'clever',
            count: courseCounts.clever,
            linkUrl: dashboardWithTab(currentSchoolIds, 'course-tab', {
              tag: COURSE_FILTER_QUERY_PARAM_TAG,
              identityProviders: ['clever'],
            }),
          },
          {
            type: 'renaissance',
            count: courseCounts.renaissance,
            linkUrl: dashboardWithTab(currentSchoolIds, 'course-tab', {
              tag: COURSE_FILTER_QUERY_PARAM_TAG,
              identityProviders: ['renaissance'],
            }),
          },
        ]}
        scrollToTabs={scrollToTabs}
        sortFirstType={sortFirstType}
      />
    </div>
  )
}

function dashboardWithTab(
  currentSchoolIds: Array<number> | undefined | null,
  activeTab: TabKeyT,
  additionalFilters?: AdditionalFiltersQueryParamsT | null,
): string {
  return Routes.dashboard({
    schoolIds: currentSchoolIds,
    activeTab,
    additionalFilters,
  })
}

function getSortFirstType({mostRecentRosterSyncEvent}: SummaryT): CountTypeT | null {
  if (mostRecentRosterSyncEvent === null) {
    return null
  } else {
    switch (mostRecentRosterSyncEvent.syncedBy.tag) {
      case 'idp':
        switch (mostRecentRosterSyncEvent.syncedBy.contents) {
          case 'clever':
            return 'clever'
          case 'class-link':
            return 'classlink'
          case 'renaissance':
            return 'renaissance'
          default:
            return null
        }
      case 'school_admin':
        return 'admin'
      case 'console_agent':
        return null
      case 'impersonation':
        return null
      default:
        return exhaustive(
          (mostRecentRosterSyncEvent as unknown as {syncedBy: {tag: never}}).syncedBy.tag,
        )
    }
  }
}
